import { Box, Heading, Text } from "@chakra-ui/react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  type SourcingEventDto,
  type TextDocumentDto,
  useCreateSourcingEventTextDocumentMutation,
  useListTextDocumentsForSourcingEventQuery,
  type ViewDocumentAsOwnerDto,
} from "../../../../../autogen/bff-api";
import { CommentSubjectKind } from "../../../../../autogen/bff-api";
import { CombinedDocuments } from "../../../../../common/documents/CombinedDocuments";
import { IncompleteFileUpload, isIncompleteFileUpload, UploadCompletedStatus } from "../../../../../common/documents/UppyUploader/FileRow";
import { useUppyUploader } from "../../../../../common/documents/UppyUploader/useUppyUploader";
import { StepNavigation } from "../../../../../common/StepNavigation";
import { SubjectCommentsSection } from "../components/commentRound/SubjectCommentsSection";

interface Props {
  eventId: string;
  event: SourcingEventDto;
  previousStep?: () => void;
  nextStep?: () => void;
  nextStepLabel?: string;
  refetch: () => void;
  isActive: boolean;
}

export const keepLocalEditsWhileSameDocumentSet = <T extends { id: string; modifiedAt?: string }>(
  current: T[],
  server: T[],
): T[] => {
  const sameDocumentSet =
    current.length === server.length && server.every((serverDoc) => current.some((doc) => doc.id === serverDoc.id));
  if (!sameDocumentSet) return server;
  const merged = current.map((doc) => {
    const serverDoc = server.find((candidate) => candidate.id === doc.id);
    return serverDoc && savedLaterOnServer(doc, serverDoc) ? serverDoc : doc;
  });
  return merged.every((doc, index) => doc === current[index]) ? current : merged;
};

const savedLaterOnServer = (local: { modifiedAt?: string }, server: { modifiedAt?: string }): boolean =>
  !!local.modifiedAt && !!server.modifiedAt && Date.parse(server.modifiedAt) > Date.parse(local.modifiedAt);

export const DocumentsStep = ({ eventId, event, previousStep, nextStep, nextStepLabel, refetch, isActive }: Props) => {
  const { t } = useTranslation();

  const { uppy, allFiles, documents, setDocuments, removeUpload } = useUppyUploader({
    entityType: "SourcingEventV2",
    entityId: eventId,
    reloadEntity: refetch,
  });

  const [createTextDocument, { isLoading: isCreatingTextDocument }] = useCreateSourcingEventTextDocumentMutation();
  const { data: textDocumentRes, isLoading: isLoadingTextDocuments } = useListTextDocumentsForSourcingEventQuery({
    eventId,
  });
  const [textDocuments, setTextDocuments] = useState<TextDocumentDto[]>([]);

  useEffect(() => {
    if (isActive) refetch();
  }, [isActive, refetch]);

  useEffect(() => {
    const serverDocuments = event.eventData.documents ?? [];
    setDocuments((current) => keepLocalEditsWhileSameDocumentSet(current, serverDocuments));
  }, [event.eventData.documents, setDocuments]);

  useEffect(() => {
    if (!textDocumentRes) return;
    setTextDocuments((current) => keepLocalEditsWhileSameDocumentSet(current, textDocumentRes.documents));
  }, [textDocumentRes]);

  const createTextDocumentForEvent = useCallback(
    () =>
      createTextDocument({
        createSourcingEventTextDocumentRequest: { eventId, name: "", text: "", tags: [] },
      }).unwrap(),
    [createTextDocument, eventId]
  );

  return (
    <Box pb="8" pt="10">
      <Heading as="h2" size="md" mb="1">
        {t("Documents")}
      </Heading>
      <Text fontSize="sm" color="smMuted" mb="6" maxW="3xl">
        {event.eventContext.type === "MiniCompetition"
          ? t(
              "Upload files or write a document from a template. These apply to this mini-competition only — the framework agreement's own documents already reach the suppliers.",
            )
          : t("Upload files or write a document from a template.")}
      </Text>

      <SubjectCommentsSection
        kind={CommentSubjectKind.Document}
        title={t("Comments on the documents from the comment round")}
        items={(event.eventData.documents ?? []).map((document) => ({ ref: document.id, label: document.name }))}
      />

      <CombinedDocuments
        uploadedDocuments={documents}
        setUploadedDocuments={setDocuments}
        textDocuments={textDocuments}
        setTextDocuments={setTextDocuments}
        createTextDocument={createTextDocumentForEvent}
        isCreatingTextDocument={isCreatingTextDocument}
        canEditTextDocuments
        isLoading={isLoadingTextDocuments}
        removeUploadedDocument={(document: ViewDocumentAsOwnerDto) =>
          removeUpload({
            status: UploadCompletedStatus.UploadCompleted,
            documentId: document.id,
            uploadId: document.fromUploadId,
            ...document,
          })
        }
        isTagsColumnVisible
        isCategoryColumnVisible
        canEditCategory
        canDeleteDocuments
        canEditDocumentNames
        documentUploadConfig={{
          uppy,
          dropTargetId: `documents-step-${eventId}`,
          dropTargetHelperText: t("Drag and drop files here, or click to browse"),
          uploads: allFiles.filter(isIncompleteFileUpload) as IncompleteFileUpload[],
          removeUpload,
        }}
      />

      <StepNavigation onPrevious={previousStep} onNext={nextStep} nextLabel={nextStepLabel} />
    </Box>
  );
};
